import styled from "styled-components";
import {
  Badge,
  Flex,
  Icon,
  Notification,
  P,
  Small,
  type FlexProps,
} from "orcs-design-system";

import {
  checkTitle,
  realRuleName,
  useGuardrailRuleLabels,
  type RuleLabel,
} from "./guardrailRuleLabels";
import { GuardrailSeverityIcon } from "./GuardrailSeverityIcon";
import type {
  GuardrailCheck,
  HeaderBadge,
} from "./RequisitionGuardrailOutcome";

type CheckTone = "pass" | "warning" | "hard";

/**
 * A routing rule that did not match did nothing to the chain. The row says
 * only "Did not pass" under a green check, so it is noise. A match stays: it
 * explains the added or skipped step or the fast-track.
 */
const isRoutingMiss = (check: GuardrailCheck): boolean => {
  if (check.state !== "FAIL") {
    return false;
  }
  const effect = check.effect ?? "BLOCK";
  switch (effect) {
    case "BLOCK":
    case "WARN":
      return false;
    case "ADD_APPROVAL_STEP":
    case "SKIP_APPROVAL_STEP":
    case "AUTO_APPROVE":
      return true;
    default: {
      const unexpected: never = effect;
      return unexpected;
    }
  }
};

/** Same rows the checklist renders. Routing misses are not failures. */
export const visibleGuardrailChecks = (
  checks: GuardrailCheck[]
): GuardrailCheck[] => checks.filter((check) => !isRoutingMiss(check));

const checkTone = (check: GuardrailCheck): CheckTone => {
  if (check.state === "PASS") {
    return "pass";
  }
  const effect = check.effect ?? "BLOCK";
  switch (effect) {
    case "WARN":
      return "warning";
    case "BLOCK":
      return "hard";
    case "ADD_APPROVAL_STEP":
    case "SKIP_APPROVAL_STEP":
    case "AUTO_APPROVE":
      return "pass";
    default: {
      const unexpected: never = effect;
      return unexpected;
    }
  }
};

const CheckIcon = ({ tone }: { tone: CheckTone }) => {
  switch (tone) {
    case "pass":
      return (
        <Icon
          icon="check"
          variant="success"
          fixedWidth
          role="img"
          aria-label="Pass"
        />
      );
    case "warning":
      return <GuardrailSeverityIcon effect="WARN" />;
    case "hard":
      return <GuardrailSeverityIcon effect="BLOCK" />;
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
};

const toneBackground = (
  tone: CheckTone
): "warningLightest" | "dangerLightest" | undefined => {
  switch (tone) {
    case "warning":
      return "warningLightest";
    case "hard":
      return "dangerLightest";
    case "pass":
      return undefined;
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
};

const stripFailPrefix = (detail: string, name: string): string => {
  if (name === "") {
    return detail;
  }
  const heads = [`${name}: fail — `, `${name}: fail - `, `${name}: fail`];
  for (const head of heads) {
    if (detail.startsWith(head)) {
      return detail.slice(head.length).trim();
    }
  }
  return detail;
};

/** "FTE cap: pass" is a stub. A headcount sentence is evidence and stays. */
const isPassStub = (detail: string): boolean =>
  /^pass$/i.test(detail) || /:\s*pass$/i.test(detail);

const checkBody = (check: GuardrailCheck, title: string): string => {
  const detail = check.detail.trim();
  const description = check.description?.trim() ?? "";
  switch (check.state) {
    case "PASS":
      if (description) {
        return description;
      }
      return detail === "" || isPassStub(detail) ? "Passed" : detail;
    case "FAIL":
      // Evidence stays. A bare "fail" uses the description.
      return stripFailPrefix(detail, title) || description || "Did not pass";
    case "UNKNOWN":
      return detail || description;
    default: {
      const unexpected: never = check.state;
      return unexpected;
    }
  }
};

const bodyColor = (tone: CheckTone): "warningDarker" | undefined => {
  switch (tone) {
    case "warning":
      return "warningDarker";
    case "pass":
    case "hard":
      return undefined;
    default: {
      const unexpected: never = tone;
      return unexpected;
    }
  }
};

const CheckList = styled(Flex)`
  list-style: none;
`;

/**
 * "GUARDRAILS" on the left, status badge on the right. The badge sits on the
 * card's right edge so it lines up with the page-header status badge above.
 */
export const GuardrailCardHeader = ({
  badge,
  ...flexProps
}: {
  badge: HeaderBadge | null;
} & Omit<FlexProps, "children">) => (
  <Flex
    justifyContent="space-between"
    alignItems="center"
    gap="s"
    {...flexProps}
  >
    <Small uppercase weight="bold" color="subText">
      Guardrails
    </Small>
    {badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null}
  </Flex>
);

const CheckRow = ({
  check,
  rules,
  loaded,
}: {
  check: GuardrailCheck;
  rules: RuleLabel[];
  loaded: boolean;
}) => {
  const tone = checkTone(check);
  const title = checkTitle(check, rules, loaded);
  return (
    <Flex
      as="li"
      alignItems="flex-start"
      gap="s"
      p="s"
      borderRadius="2"
      bg={toneBackground(tone)}
      data-testid={`guardrail-check-${check.ruleId}`}
      data-highlight={tone}
    >
      <CheckIcon tone={tone} />
      <Flex flexDirection="column" gap="xs" flex="1" minWidth="0">
        <Small uppercase color="subText">
          {title}
        </Small>
        <P m="0" color={bodyColor(tone)}>
          {checkBody(check, title)}
        </P>
      </Flex>
    </Flex>
  );
};

/** A failed rule, tinted. Passing rules stay in the checklist. */
export const GuardrailCheckNotice = ({
  check,
  rules,
  loaded,
}: {
  check: GuardrailCheck;
  rules: RuleLabel[];
  loaded: boolean;
}) => {
  const tone = checkTone(check);
  const title = checkTitle(check, rules, loaded);
  return (
    <Notification
      variant={tone === "hard" ? "error" : "warning"}
      closable={false}
      noIcon
      data-testid={`guardrail-check-${check.ruleId}`}
      data-highlight={tone}
    >
      <Flex alignItems="flex-start" gap="s">
        <CheckIcon tone={tone} />
        <Flex flexDirection="column" gap="xs" flex="1" minWidth="0">
          <P m="0" weight="bold">
            {title}
          </P>
          <P m="0" color={bodyColor(tone)}>
            {checkBody(check, title)}
          </P>
        </Flex>
      </Flex>
    </Notification>
  );
};

const GuardrailCheckList = ({ checks }: { checks: GuardrailCheck[] }) => {
  const shown = visibleGuardrailChecks(checks);
  const labels = useGuardrailRuleLabels(
    shown.some((check) => realRuleName(check) === "")
  );
  if (shown.length === 0) {
    return null;
  }
  return (
    <CheckList forwardedAs="ul" flexDirection="column" gap="xs" m="0" p="0">
      {shown.map((check) => (
        <CheckRow
          key={check.ruleId}
          check={check}
          rules={labels.rules}
          loaded={labels.loaded}
        />
      ))}
    </CheckList>
  );
};

export default GuardrailCheckList;
